import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { DocumentsService } from '@/api';
import type {
  DocumentsFilters,
  CreateDocumentRequest,
  UpdateDocumentRequest,
  AttachFileRequest,
  ReplaceVersionRequest,
  DocumentAccessLogsFilters,
} from '@/types/document';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react/macro';
import { errorToastText } from '@/lib/translateBackendError';

// Query key factory for documents
export const documentKeys = {
  all: ['documents'] as const,
  lists: () => [...documentKeys.all, 'list'] as const,
  list: (filters: DocumentsFilters) => [...documentKeys.lists(), filters] as const,
  details: () => [...documentKeys.all, 'detail'] as const,
  detail: (id: string) => [...documentKeys.details(), id] as const,
  versions: (id: string, limit: number, offset: number) => [...documentKeys.detail(id), 'versions', { limit, offset }] as const,
  accessLogs: (id: string, filters: DocumentAccessLogsFilters) => [...documentKeys.detail(id), 'access-logs', filters] as const,
};

/**
 * Hook to fetch paginated documents with filters.
 */
export function useDocuments(filters: DocumentsFilters = {}) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: documentKeys.list(filters),
    queryFn: () =>
      DocumentsService.getApiV1Documents(
        filters.page ?? 1,
        filters.page_size ?? 20,
        filters.category,
        filters.confidentiality,
        filters.is_published,
        filters.display_target,
        filters.search,
      ),
    enabled: isSignedIn && isAuthReady,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch a single document by ID.
 */
export function useDocument(id: string) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: documentKeys.detail(id),
    queryFn: () => DocumentsService.getApiV1Documents1(id),
    enabled: isSignedIn && isAuthReady && !!id,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch version history for a document.
 */
export function useDocumentVersions(documentId: string, limit = 50, offset = 0) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: documentKeys.versions(documentId, limit, offset),
    queryFn: () => DocumentsService.getApiV1DocumentsVersions(documentId, limit, offset),
    enabled: isSignedIn && isAuthReady && !!documentId,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch access logs for a document.
 */
export function useDocumentAccessLogs(documentId: string, filters: DocumentAccessLogsFilters = {}) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: documentKeys.accessLogs(documentId, filters),
    queryFn: () =>
      DocumentsService.getApiV1DocumentsAccessLogs(
        documentId,
        filters.page ?? 1,
        filters.page_size ?? 20,
      ),
    enabled: isSignedIn && isAuthReady && !!documentId,
    staleTime: 30000,
  });
}

/**
 * Hook to create a new document.
 */
export function useCreateDocument() {
  const queryClient = useQueryClient();
  const { t, i18n } = useLingui();

  return useMutation({
    mutationFn: (data: CreateDocumentRequest) =>
      DocumentsService.postApiV1Documents(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
      toast.success(t({ id: 'documents.create.success', message: 'Document created successfully' }));
    },
    onError: (error) => {
      toast.error(errorToastText(error, i18n, t({ id: 'documents.create.error', message: 'Failed to create document' })));
    },
  });
}

/**
 * Hook to update a document.
 */
export function useUpdateDocument() {
  const queryClient = useQueryClient();
  const { t, i18n } = useLingui();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDocumentRequest }) =>
      DocumentsService.putApiV1Documents(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
      toast.success(t({ id: 'documents.update.success', message: 'Document updated successfully' }));
    },
    onError: (error) => {
      toast.error(errorToastText(error, i18n, t({ id: 'documents.update.error', message: 'Failed to update document' })));
    },
  });
}

/**
 * Hook to delete a document.
 */
export function useDeleteDocument() {
  const queryClient = useQueryClient();
  const { t, i18n } = useLingui();

  return useMutation({
    mutationFn: (id: string) => DocumentsService.deleteApiV1Documents(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
      toast.success(t({ id: 'documents.delete.success', message: 'Document deleted successfully' }));
    },
    onError: (error) => {
      toast.error(errorToastText(error, i18n, t({ id: 'documents.delete.error', message: 'Failed to delete document' })));
    },
  });
}

/**
 * Hook to attach a file to a document. `silent` suppresses the success toast for a
 * caller that attaches the file as part of a document save and reports once.
 */
export function useAttachFile(options?: { silent?: boolean }) {
  const silent = options?.silent ?? false;
  const queryClient = useQueryClient();
  const { t, i18n } = useLingui();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AttachFileRequest }) =>
      DocumentsService.postApiV1DocumentsAttachFile(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
      if (!silent) toast.success(t({ id: 'documents.attachFile.success', message: 'File attached successfully' }));
    },
    onError: (error) => {
      toast.error(errorToastText(error, i18n, t({ id: 'documents.attachFile.error', message: 'Failed to attach file' })));
    },
  });
}

/**
 * Hook to replace document with a new version. `silent` suppresses the success toast for a
 * caller that attaches the file as part of a document save and reports once.
 */
export function useReplaceVersion(options?: { silent?: boolean }) {
  const silent = options?.silent ?? false;
  const queryClient = useQueryClient();
  const { t, i18n } = useLingui();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReplaceVersionRequest }) =>
      DocumentsService.postApiV1DocumentsVersions(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
      if (!silent) toast.success(t({ id: 'documents.replaceVersion.success', message: 'New version uploaded successfully' }));
    },
    onError: (error) => {
      toast.error(errorToastText(error, i18n, t({ id: 'documents.replaceVersion.error', message: 'Failed to upload new version' })));
    },
  });
}
